import * as React from 'react';
import { RenderElementProps } from 'slate-react';
import styled from 'styled-components';

import { flexGap, horizontalSpace } from '@doltech/utils/lib/css-style';

import { useFromScreens } from '../../../hooks/useDeviceQuery.hook';
import { useRootElement } from '../../../slatePlugins/common/hocs/current-element.store';
import { findBellowByType } from '../../../figma/DolEditor/helpers/nodeValue';
import { colorsV2 } from '../../../figma/colors-v2';
import { ResponsiveTypography } from '../../../figma/Typography/v3/ResponsiveTypography';
import { QuestionNumberInlineItem } from '../../../figma/QuestionNumberInlineItem/QuestionNumberInlineItem';
import { ExerciseBlockFieldItemDropdown } from '../../../figma/BlogEditorBlogSystem/ExerciseBlockFieldItem/ExerciseBlockFieldItemDropdown/ExerciseBlockFieldItemDropdown';

import { CORRECT_ANSWER, BLANK_V2 } from '../../utils/types';
import { useProgressListGrammar } from '../../hocs/progress-list-grammar';
import { useOptionListWithHint } from '../useOptionListWithHint';
import { EditorDrawerLayout } from '../../layouts/EditorDrawerLayout';

const DrawerOptionList = styled.div`

  .drawer-option-item {
    padding: 8px 16px;
    cursor: pointer;
    user-select: none;

    &:first-child {
      border-top: 1px solid ${colorsV2.neutral20};
    }

    &:not(:last-child) {
      border-bottom: 1px solid ${colorsV2.neutral20};
    }

    &:active {
      background-color: ${colorsV2.neutral20};
    }
  }
`;

const InputDropdown = styled.span`
  display: inline-flex;
  align-items: center;
  ${flexGap(4)}
`;

const Container = styled.span`
  display: inline-block;
  ${horizontalSpace(4)}

  margin: 0 6px;
`;

export const GapFillWithHintBlank = (props: RenderElementProps) => {
  const { attributes, children, element } = props;
  const rootElement = useRootElement();
  const progressStore = useProgressListGrammar();
  const questionId = element.id;
  const { dropDownOptions } = useOptionListWithHint() || {};
  const [, from1024] = useFromScreens([0, 1024]);
  const [drawerOpened, setDrawerOpened] = React.useState(false);

  const blanks = findBellowByType(rootElement, BLANK_V2);
  const blankIndex = blanks.findIndex((eachBlank) => eachBlank.id === element.id);
  const blankOrder = blankIndex + (rootElement?.startFrom ?? 1);

  const options = dropDownOptions
    ?.map((eachOption) => ({
      key: eachOption.id,
      value: eachOption.displayName,
    }));

  const currentAnswer = progressStore?.userAnswers?.find((item) => item?.questionId === questionId);
  const correctAnswerId = findBellowByType(rootElement, CORRECT_ANSWER)
    .filter((n) => n.forId === element.id)
    ?.[0]?.correctAnswer;

  const isCorrect = currentAnswer?.isCorrect;
  const isMissed = !currentAnswer?.answerId;

  const handleUserAnswers = (answerId) => {
    progressStore.setUserAnswers((prevUserAnswers) => {
      const updatedAnswer = {
        questionId,
        answerId,
      };
      const existUserAnswer = prevUserAnswers?.find((eachAnswer) => eachAnswer?.questionId === questionId);

      if (existUserAnswer) {
        return prevUserAnswers?.map((eachAnswer) => {
          if (questionId === eachAnswer?.questionId) {
            return updatedAnswer;
          }
          return eachAnswer;
        });
      }
      return [
        ...prevUserAnswers,
        updatedAnswer,
      ];
    });
  };

  if (progressStore?.isShowResult) {
    if (isMissed) {
      return (
        <Container className="gap-fill-with-hint-blank missed" {...attributes}>
          <QuestionNumberInlineItem order={blankOrder} />
          <InputDropdown className="result">
            <ExerciseBlockFieldItemDropdown
              className="block-dropdown"
              status="correct"
              options={options}
              value={correctAnswerId}
              readonly
            />
            <ResponsiveTypography.Paragraph variant="regular/16-24" color="neutral120">
              Missed
            </ResponsiveTypography.Paragraph>
          </InputDropdown>
        </Container>
      );
    }

    if (isCorrect) {
      return (
        <Container className="gap-fill-with-hint-blank correct" {...attributes}>
          <QuestionNumberInlineItem order={blankOrder} />
          <InputDropdown className="result">
            <ExerciseBlockFieldItemDropdown
              className="block-dropdown"
              status="correct"
              options={options}
              value={currentAnswer?.answerId}
              readonly
            />
          </InputDropdown>
        </Container>
      );
    }

    return (
      <Container className="gap-fill-with-hint-blank incorrect" {...attributes}>
        <QuestionNumberInlineItem order={blankOrder} />
        <InputDropdown className="result">
          <ExerciseBlockFieldItemDropdown
            className="block-dropdown"
            status="incorrect"
            options={options}
            value={currentAnswer?.answerId}
            readonly
          />
          <ExerciseBlockFieldItemDropdown
            className="block-dropdown"
            status="correct"
            options={options}
            value={correctAnswerId}
            readonly
          />
        </InputDropdown>
      </Container>
    );
  }

  if (from1024) {
    return (
      <Container className="gap-fill-with-hint-blank" {...attributes}>
        <QuestionNumberInlineItem order={blankOrder} />
        <InputDropdown className="result">
          <ExerciseBlockFieldItemDropdown
            className="block-dropdown"
            mode="popover"
            options={options}
            value={currentAnswer?.answerId}
            onChange={handleUserAnswers}
          />
        </InputDropdown>
        {children}
      </Container>
    );
  }

  return (
    <Container className="gap-fill-with-hint-blank" {...attributes}>
      <QuestionNumberInlineItem order={blankOrder} />
      <InputDropdown className="result">
        <ExerciseBlockFieldItemDropdown
          className="block-dropdown"
          mode="action"
          options={options}
          value={currentAnswer?.answerId}
          onClick={() => setDrawerOpened(true)}
        />
      </InputDropdown>
      <EditorDrawerLayout
        title="Option list"
        onClose={(e) => {
          e.stopPropagation();
          setDrawerOpened(false);
        }}
        visible={drawerOpened}
      >
        <DrawerOptionList className="drawer-option-list">
          {dropDownOptions?.map(option => (
            <div key={option.id} className="drawer-option-item" onClick={() => {
              handleUserAnswers(option?.id);
              setDrawerOpened(false);
            }}>
              <ResponsiveTypography.Paragraph variant="regular/16-24" color="neutral180">
                {option?.displayName}
              </ResponsiveTypography.Paragraph>
            </div>
          ))}
        </DrawerOptionList>
      </EditorDrawerLayout>
      {children}
    </Container>
  );
};
